import envs from "env";

import type { GlobalConfigs } from "./types";

interface InjectedConfigs {
  enableGoogleOAuth: boolean;
  enableGithubOAuth: boolean;
  enableRapidAPI: boolean;
  fusioncharts: {
    licenseKey: string;
  };
  google: string;
  enableTNCPP: boolean;
  cloudHosting: boolean;
  algolia: {
    apiId: string;
    apiKey: string;
    indexName: string;
  };
  logLevel: "debug" | "error";
  appVersion: {
    id: string;
    releaseDate: string;
  };
  intercomAppID: string;
  mailEnabled: boolean;
}

declare global {
  interface Window {
    SUPERBLOCKS_CONFIGS: InjectedConfigs;
    Intercom: any;
    dataLayer: any; // this is used by Google Tag manager
  }
}

const getConfigsFromEnvVars = (): InjectedConfigs => {
  return {
    enableGoogleOAuth: process.env.REACT_APP_OAUTH2_GOOGLE_CLIENT_ID
      ? process.env.REACT_APP_OAUTH2_GOOGLE_CLIENT_ID.length > 0
      : false,
    enableGithubOAuth: process.env.REACT_APP_OAUTH2_GITHUB_CLIENT_ID
      ? process.env.REACT_APP_OAUTH2_GITHUB_CLIENT_ID.length > 0
      : false,
    fusioncharts: {
      licenseKey: process.env.REACT_APP_FUSIONCHARTS_LICENSE_KEY || "",
    },
    algolia: {
      apiId: process.env.REACT_APP_ALGOLIA_API_ID || "",
      apiKey: process.env.REACT_APP_ALGOLIA_API_KEY || "",
      indexName: process.env.REACT_APP_ALGOLIA_SEARCH_INDEX_NAME || "",
    },
    logLevel:
      (process.env.REACT_APP_CLIENT_LOG_LEVEL as
        | "debug"
        | "error"
        | undefined) || "debug",
    google: envs.get("SUPERBLOCKS_UI_GOOGLE_MAPS_API_KEY"),
    enableTNCPP: process.env.REACT_APP_TNC_PP
      ? process.env.REACT_APP_TNC_PP.length > 0
      : false,
    enableRapidAPI: process.env.REACT_APP_MARKETPLACE_URL
      ? process.env.REACT_APP_MARKETPLACE_URL.length > 0
      : false,
    cloudHosting: process.env.REACT_APP_CLOUD_HOSTING
      ? process.env.REACT_APP_CLOUD_HOSTING.length > 0
      : false,
    appVersion: {
      id: process.env.REACT_APP_VERSION_ID || "",
      releaseDate: process.env.REACT_APP_VERSION_RELEASE_DATE || "",
    },
    intercomAppID: process.env.REACT_APP_INTERCOM_APP_ID || "",
    mailEnabled: process.env.REACT_APP_MAIL_ENABLED
      ? process.env.REACT_APP_MAIL_ENABLED.length > 0
      : false,
  };
};

const getConfig = (fromENV: string, fromWindow: string) => {
  if (fromWindow.length > 0) return { enabled: true, value: fromWindow };
  else if (fromENV.length > 0) return { enabled: true, value: fromENV };
  return { enabled: false, value: "" };
};

// TODO(Abhinav): See if this is called so many times, that we may need some form of memoization.
export const getGlobalConfigs = (): GlobalConfigs => {
  const { SUPERBLOCKS_CONFIGS } = window;
  const ENV_CONFIG = getConfigsFromEnvVars();

  const fusioncharts = getConfig(
    ENV_CONFIG.fusioncharts.licenseKey,
    SUPERBLOCKS_CONFIGS.fusioncharts.licenseKey,
  );
  const google = getConfig(ENV_CONFIG.google, SUPERBLOCKS_CONFIGS.google);

  const algoliaAPIID = getConfig(
    ENV_CONFIG.algolia.apiId,
    SUPERBLOCKS_CONFIGS.algolia.apiId,
  );
  const algoliaAPIKey = getConfig(
    ENV_CONFIG.algolia.apiKey,
    SUPERBLOCKS_CONFIGS.algolia.apiKey,
  );
  const algoliaIndex = getConfig(
    ENV_CONFIG.algolia.indexName,
    SUPERBLOCKS_CONFIGS.algolia.indexName,
  );

  return {
    fusioncharts: {
      enabled: fusioncharts.enabled,
      licenseKey: fusioncharts.value,
    },
    algolia: {
      enabled: true,
      apiId: algoliaAPIID.value || "",
      apiKey: algoliaAPIKey.value || "",
      indexName: algoliaIndex.value || "",
    },
    google: {
      enabled: google.enabled,
      apiKey: google.value,
    },
    enableRapidAPI:
      ENV_CONFIG.enableRapidAPI || SUPERBLOCKS_CONFIGS.enableRapidAPI,
    enableGithubOAuth:
      ENV_CONFIG.enableGithubOAuth || SUPERBLOCKS_CONFIGS.enableGithubOAuth,
    enableGoogleOAuth:
      ENV_CONFIG.enableGoogleOAuth || SUPERBLOCKS_CONFIGS.enableGoogleOAuth,
    cloudHosting: ENV_CONFIG.cloudHosting || SUPERBLOCKS_CONFIGS.cloudHosting,
    logLevel: ENV_CONFIG.logLevel || SUPERBLOCKS_CONFIGS.logLevel,
    enableTNCPP: ENV_CONFIG.enableTNCPP || SUPERBLOCKS_CONFIGS.enableTNCPP,
    appVersion: ENV_CONFIG.appVersion || SUPERBLOCKS_CONFIGS.appVersion,
    intercomAppID:
      ENV_CONFIG.intercomAppID || SUPERBLOCKS_CONFIGS.intercomAppID,
    mailEnabled: ENV_CONFIG.mailEnabled || SUPERBLOCKS_CONFIGS.mailEnabled,
  };
};
